import { DialogClose } from '@/components/_ui/Dialog'
import useGlobalStore from '@/store/useGlobalStore'
import Image from 'next/image'
import { useEffect, useMemo, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import T, { useTranslate } from '@/components/Helpers/Translate'
import type {
  CosmicProductType,
  CosmicVariationType,
} from '@/lib/cms/cosmic/types/CosmicProductType'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import { useProductVariationTypeQueryParams } from '@/lib/hooks/helpers/useProductVariationTypeQueryParams'
import { useDoesUserHaveActiveSubscription } from '@/lib/hooks/services/useDoesUserHaveActiveSubscription'
import { useGetSkuAvailability } from '@/lib/hooks/services/useGetSkuAvailability'
import { getTerms } from '@/lib/hooks/services/useGetTerms'
import {
  generateAddToCartEventData,
  generateProductEventData,
} from '@/lib/services/mixpanel/helpers/generateCartEventData'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { getDefaultSelectedVariation } from '@/lib/utils/product/getDefaultSelectedVariation'
import { getProductRecommendationSlugArray } from '@/lib/utils/shared/getProductRecommendationSlugArray'
import type { CartEventLocation, CartItemType } from '@/store/types/cart'
import { useRouter } from 'next/router'
import { useFocusMode } from '@/lib/hooks/helpers/useFocusMode'
import { ProductVariantFlag } from '@/components/_products/ProductVariantFlag'
import { Button } from '../_ui/Button'
import { ErrorAlert } from '../_ui/ErrorAlert'
import AddToCartButton from '../AddToCartButton/AddToCartButton'
import { useModals } from '../ModalManager/useModals'
import { AddProductToSubscription } from './_components/AddProductToSubscription'
import FrequentlyBoughtTogether from './_components/FrequentlyBoughtTogether'
import ProductCarousel from './_components/ProductCarousel'
import ProductDelivery from './_components/ProductDelivery'
import { ProductFlavorSelector } from './_components/ProductFlavorSelector'
import { ProductPurchaseOptions } from './_components/ProductPurchaseOptions'
import { getDefaultSelectedPurchaseOption } from './_components/ProductPurchaseOptions/_helpers/getDefaultSelectedPurchaseOption'
import ProductShareButton from './_components/ProductShareButton'
import { ProductPageSections } from './_sections'

type Props = {
  productData: CosmicProductType
}

export const ProductDetails = ({ productData }: Props) => {
  const {
    app: { addProductToSubscription },
    setApp,
    setAppAddProductToSubscription,
    addToCart,
    setUser,
    user: { customerType, isSignedIn },
    app: { marketExceptions },
  } = useGlobalStore()

  useDoesUserHaveActiveSubscription()

  const { isLoading: isLoadingProductAvailability, productAvailability } =
    useGetSkuAvailability({
      product: productData,
    })

  const { modals } = useModals()
  const { query } = useRouter()
  const { translate } = useTranslate()
  const { gtm } = useGTM()

  const { isFocusMode, isLoadingIsFocusMode } = useFocusMode()

  const [itemCount, setItemCount] = useState(1)

  const isAssociate = customerType === 'Associate'

  useEffect(() => {
    if (!query.referralCode) return

    setUser({
      refID: query.referralCode as string,
      refName: null,
      customerType: 'Customer',
    })
  }, [query.referralCode, setUser])

  const isAccessory = useMemo(
    () =>
      !!productData.categories?.find(
        (categoryData) => categoryData.slug === 'accessories',
      ),
    [productData.categories],
  )

  const [selectedVariation, setSelectedVariation] =
    useState<CosmicVariationType | null>(
      productData.variations
        ? getDefaultSelectedVariation({
            variations: productData.variations,
            query,
          })
        : null,
    )

  const [selectedPurchaseOption, setSelectedPurchaseOption] = useState<
    string | null
  >(
    getDefaultSelectedPurchaseOption({
      purchaseOptions: productData.purchaseOptions,
    }),
  )

  useEffect(() => {
    mixpanelService
      .trackEvent(MixpanelEvent.VIEW_PRODUCT, {
        cart: generateProductEventData(
          productData,
          selectedVariation ||
            getDefaultSelectedVariation({
              variations: productData.variations,
              query,
            }),
          marketExceptions?.currencyCode || 'USD',
          getTerms({
            forCustomerType: customerType,
            pricingData: selectedVariation?.pricing || null,
          })?.priceEach || 0,
          {
            purchase_type:
              getDefaultSelectedPurchaseOption({
                purchaseOptions: productData.purchaseOptions,
              }) || '',
          },
        ),
        focusMode: isFocusMode,
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariation, isFocusMode])

  useEffect(() => {
    // setSelectedVariation(
    //   productData.variations
    //     ? getDefaultSelectedVariation({
    //         variations: productData.variations,
    //         query,
    //       })
    //     : null,
    // )
    setTimeout(() => {
      setSelectedVariation(
        productData.variations
          ? getDefaultSelectedVariation({
              variations: productData.variations,
              query,
            })
          : null,
      )

      setItemCount(1)
    }, 0)
  }, [productData, query])

  useProductVariationTypeQueryParams({ selectedVariation })

  const selectedPv = getTerms({
    forCustomerType: customerType,
    pricingData: selectedVariation?.pricing || null,
  })?.pvEach

  const handleDisplayPartsData = () => {
    const partsData = selectedVariation?.partsLabel

    if (partsData) {
      return (
        <p>
          {partsData}
          <span className="mx-3">|</span>
        </p>
      )
    }

    // if (partsData?.quantity && partsData?.unit) {
    //   return (
    //     <p>
    //       {partsData.quantity} {partsData.unit}
    //       <span className="mx-3">|</span>
    //     </p>
    //   )
    // }

    return null
  }

  const ProductDetailsSubtitle = () => (
    <div className="flex">
      {handleDisplayPartsData()}

      {isAssociate ? (
        <>
          <p>
            {translate('pv')}: {selectedPv}
          </p>
          <span className="mx-3">|</span>
        </>
      ) : null}
      <p>
        {translate('sku')}: {selectedVariation?.sku}
      </p>
    </div>
  )

  // TODO: have to add items from the product data as they do not exist in variationEntry. For example images only exist within the variation flavor but not every item has variations.

  const handleIncrementQuantity = () => {
    setItemCount(itemCount + 1)
  }

  const handleDecrementQuantity = () => {
    if (itemCount > 1) {
      setItemCount(itemCount - 1)
    }
  }

  const openGuaranteeDialog = () => {
    if (!productData.policies) return

    modals.openModal({
      type: 'modal',
      modalId: 'our_money_back_guarantee_checkout',
      modal: {
        titleIcon: productData.policies[0].icon,
        className: 'max-w-3xl',

        children: (
          <div>
            <div
              dangerouslySetInnerHTML={{
                __html: productData.policies[0].content,
              }}
            ></div>

            <DialogClose>
              <Button className="mt-4 p-0 underline" variant="link">
                <T>Close</T>
              </Button>
            </DialogClose>
          </div>
        ),
      },
    })
  }

  const renderProductDescription = () => {
    if (!selectedVariation?.description) {
      return (
        <ErrorAlert
          error={{
            error: {
              message: 'Missing product description',
            },
          }}
        />
      )
    }

    return (
      <div
        dangerouslySetInnerHTML={{
          __html: selectedVariation.description,
        }}
        className="mt-4 block leading-[30px]"
        data-test="product-description"
      />
    )
  }

  if (!selectedVariation) {
    return (
      <ErrorAlert
        error={{
          error: {
            message: 'Unable to load product variation.',
            code: 400,
          },
        }}
      />
    )
  }

  const itemToAddToCart: CartItemType = {
    subscriptionDiscount: productData.subscriptionDiscount || 0,
    slug: productData.slug,
    title: productData.name,
    image:
      selectedVariation.media?.imageUrls?.[0]?.url ||
      productData.imageUrl ||
      '',
    description: productData.description,
    po: selectedPurchaseOption === 'onetime' ? 'otp' : 'subscribe',
    qty: itemCount,
    product: selectedVariation,
    purchaseOptions: productData.purchaseOptions || null,
    guarantee: productData.policies?.[0] || null,
    recommendedProducts: getProductRecommendationSlugArray({
      productData,
    }),
    addToCartLocation: ['product_page_main_button'],
  }

  return (
    <>
      <AddProductToSubscription
        isLoadingProductAvailability={isLoadingProductAvailability}
        productAvailability={productAvailability}
        itemToAddToSubscription={itemToAddToCart}
      />
      <div className="mt-4 flex h-auto w-full flex-col gap-7 text-primary md:max-w-[88rem] md:flex-row">
        {selectedVariation.media && (
          <ProductCarousel
            images={
              selectedVariation.media.imageUrls ?? selectedVariation.media
            }
            flagData={selectedVariation.flag}
            className="hidden md:block"
            selectedVariation={selectedVariation}
            productData={productData}
          />
        )}

        <div className="mb-5 flex w-full flex-col overflow-x-hidden md:w-1/2 md:pr-10">
          <div className="relative flex w-full flex-col justify-between md:flex-row">
            <h1
              className="mb-2 text-2xl font-semibold lg:text-4xl"
              data-test="product-title"
            >
              {productData.name}
            </h1>

            <span className="inline md:hidden">
              <ProductDetailsSubtitle />
              {isSignedIn && (
                <ProductShareButton
                  product={productData}
                  selectedVariation={selectedVariation}
                />
              )}
            </span>

            <div className="relative mt-3">
              {selectedVariation.flag?.label && (
                <ProductVariantFlag
                  text={selectedVariation.flag.label ?? ''}
                  textColor={selectedVariation.flag.style?.textColor ?? ''}
                  backgroundColor={
                    selectedVariation.flag.style?.backgroundColor ?? ''
                  }
                  className="absolute -top-2 right-0 md:hidden"
                />
              )}

              {selectedVariation.media && (
                <ProductCarousel
                  images={
                    selectedVariation.media.imageUrls ?? selectedVariation.media
                  }
                  className="md:hidden"
                  selectedVariation={selectedVariation}
                  productData={productData}
                />
              )}
            </div>
          </div>

          <span className="hidden  md:inline">
            <ProductDetailsSubtitle />
            {isSignedIn && (
              <ProductShareButton
                product={productData}
                selectedVariation={selectedVariation}
                className=""
              />
            )}
          </span>
          {renderProductDescription()}

          <ErrorBoundary
            fallback={
              <ErrorAlert
                error={{
                  error: {
                    code: 500,
                    message: 'Error loading flavor selector',
                  },
                }}
              />
            }
          >
            <div className="py-6" data-test="product-variants">
              {productData?.variations && (
                <ProductFlavorSelector
                  attributes={productData.attributes}
                  variations={productData.variations}
                  selectedVariation={selectedVariation}
                  setSelectedVariation={setSelectedVariation}
                />
              )}
            </div>
          </ErrorBoundary>

          <ProductPurchaseOptions
            selectedVariation={selectedVariation}
            selectedPurchaseOption={selectedPurchaseOption}
            setSelectedPurchaseOption={setSelectedPurchaseOption}
            purchaseOptions={productData.purchaseOptions}
            subscriptionDiscount={productData.subscriptionDiscount}
          />

          <ProductDelivery
            isLoadingProductAvailability={isLoadingProductAvailability}
            productAvailability={productAvailability}
            selectedVariation={selectedVariation}
            product={productData}
          />

          <div className="mt-6 flex w-full flex-row justify-between gap-3 md:gap-16">
            <div className="flex h-[50px] w-[100px] items-center justify-between rounded-full border border-secondary md:w-[152px]">
              <Button
                onClick={() => handleDecrementQuantity()}
                variant="icon"
                className="ml-2 rounded-full p-1 md:ml-3"
                data-test="product-quantity-decrement"
              >
                <Image
                  src="/icons/minus.svg"
                  height={20}
                  width={20}
                  alt="Minus"
                />
              </Button>
              <span data-test="product-quantity">{itemCount}</span>
              <Button
                onClick={() => handleIncrementQuantity()}
                variant="icon"
                className="mr-2 rounded-full p-1 md:mr-3"
                data-test="product-quantity-increment"
              >
                <Image
                  src="/icons/plus.svg"
                  height={20}
                  width={20}
                  alt="Plus"
                />
              </Button>
            </div>
            <AddToCartButton
              disabled={
                !selectedVariation ||
                !selectedVariation.pricing ||
                selectedVariation.isDisable
              }
              className="mt-0 h-[50px] w-2/3"
              data-test="product-add-to-cart"
              onClick={() => {
                if (
                  itemToAddToCart.po === 'subscribe' &&
                  addProductToSubscription.userHasActiveSubscription
                ) {
                  setAppAddProductToSubscription({
                    isSelectActiveSubscriptionModalOpen: true,
                  })

                  return
                }

                gtm.addToCart({
                  cartItem: itemToAddToCart,
                  event_location: 'product_page_main_button',
                })

                const addToCartLocation: CartEventLocation[] = []
                for (let index = 1; index <= itemCount; index += 1) {
                  addToCartLocation.push('product_page_main_button')
                }
                mixpanelService
                  .trackEvent(MixpanelEvent.ADD_TO_CART, {
                    event_location: 'product_page_main_button',
                    cart: generateAddToCartEventData({
                      cartItem: { ...itemToAddToCart, addToCartLocation },
                      currency: marketExceptions?.currencyCode || 'USD',
                      price:
                        getTerms({
                          forCustomerType: customerType,
                          pricingData: selectedVariation?.pricing || null,
                        })?.priceEach || 0,
                      quantity: itemCount,
                      addToCartLocation: 'product_page_main_button',
                      focus: isFocusMode,
                    }),
                    item_count: itemCount,
                  })
                  .catch(() => {})

                setApp({ isCartOpen: true })
                addToCart({ ...itemToAddToCart, addToCartLocation })
              }}
            >
              <T>add_to_cart</T>
            </AddToCartButton>
          </div>
          <div className="mt-4 flex flex-row justify-end">
            {productData.policies && productData.policies[0] && (
              <Button
                className="h-full p-0 font-normal text-[#0B3B6199] underline"
                onClick={() => openGuaranteeDialog()}
                variant="link"
                data-test="product-guarantee"
              >
                {productData.policies[0].buttonText}
              </Button>
            )}
          </div>

          {/* Don't show frequently bought products in focus mode or if loading focus mode */}
          {!isFocusMode && !isLoadingIsFocusMode && (
            <FrequentlyBoughtTogether productData={productData} />
          )}
        </div>
      </div>
      <ProductPageSections
        product={productData}
        selectedVariation={selectedVariation}
        belowFoldSections={productData.contents}
        isAccessory={isAccessory}
      />
    </>
  )
}
